/**
 * CihTabNav — sticky in-page anchor nav. Each tab links to a `#{id}` on the
 * same page. A tab with `count === 0` is suppressed entirely.
 *
 * Scroll-spy: listens to scroll position and adds an `active` indicator to
 * the tab whose section top is closest to (but above) the viewport top.
 *
 * Aliases: tab nav, page tabs, in-page nav
 */

import { useEffect, useMemo, useState } from "react";

import { cn } from "~/lib/cn";

export type CihTab = {
  readonly id: string;
  readonly label: string;
  readonly count?: number;
};

type Props = {
  readonly tabs: ReadonlyArray<CihTab>;
  readonly variant?: "light" | "dark";
};

export function CihTabNav({ tabs, variant = "light" }: Props) {
  const visible = useMemo(
    () => tabs.filter((t) => t.count === undefined || t.count > 0),
    [tabs],
  );
  const [active, setActive] = useState<string | null>(visible[0]?.id ?? null);

  useEffect(() => {
    if (visible.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
            return;
          }
        }
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );

    for (const tab of visible) {
      const el = document.getElementById(tab.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [visible]);

  if (visible.length === 0) return null;

  const isDark = variant === "dark";

  return (
    <nav
      aria-label="Page sections"
      className={cn(
        // Docks under the public header while it's on screen (mobile only).
        "sticky top-[var(--dcm-sticky-offset)] z-30 transition-[top] duration-300 ease-out motion-reduce:transition-none",
        isDark
          ? "border-b border-purple-900/30 bg-gray-950/95 backdrop-blur"
          : "border-b border-gray-200 bg-white/95 backdrop-blur",
      )}
    >
      <div className="mx-auto flex max-w-7xl gap-7 overflow-x-auto px-4 md:gap-9 xl:px-0">
        {visible.map((tab) => (
          <a
            key={tab.id}
            href={`#${tab.id}`}
            aria-current={active === tab.id ? "true" : undefined}
            className={cn(
              "flex shrink-0 items-center gap-1.5 border-b-2 py-3.5 text-sm font-medium",
              "focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2",
              isDark
                ? "focus-visible:outline-white"
                : "focus-visible:outline-purple-600",
              active === tab.id
                ? isDark
                  ? "border-white text-white"
                  : "border-gray-900 text-gray-900"
                : isDark
                  ? "border-transparent text-purple-300/60 hover:text-white"
                  : "border-transparent text-gray-500 hover:text-gray-900",
            )}
          >
            {tab.label}
            {typeof tab.count === "number" ? (
              <span
                className={cn(
                  "rounded px-1.5 text-[10px]",
                  active === tab.id
                    ? isDark
                      ? "bg-white/10 text-white"
                      : "bg-gray-900 text-white"
                    : isDark
                      ? "bg-white/5 text-purple-300"
                      : "bg-gray-100 text-gray-600",
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </a>
        ))}
      </div>
    </nav>
  );
}
